/**
 * AI Chart Narrative Generator
 * 
 * Generates contextual narratives explaining what a signal is showing
 * using Claude API for natural, insightful analysis.
 */

import { SIGNAL_METADATA, CATEGORIES } from '../../api/capitulators.js';

// Cache for generated narratives: key = `${signalId}:${rangeId}:${dataHash}`
const narrativeCache = new Map();

// Generate a simple hash of chart data to detect changes
function hashChartData(chartData) {
  if (!chartData || chartData.length === 0) return 'empty';
  const first = chartData[0];
  const last = chartData[chartData.length - 1];
  return `${chartData.length}:${first?.time}:${first?.value?.toFixed(2)}:${last?.time}:${last?.value?.toFixed(2)}`;
}

/**
 * Build context object for the AI prompt
 */
function buildContext(signalId, chartData, rangeId, currentSignal) {
  const metadata = SIGNAL_METADATA[signalId] || {};
  const category = CATEGORIES[metadata.category] || {};
  
  // Calculate stats from chart data
  const values = chartData.map(d => d.value).filter(v => v != null);
  const current = values[values.length - 1];
  const high = Math.max(...values);
  const low = Math.min(...values);
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  
  // Calculate percentile position
  const sortedValues = [...values].sort((a, b) => a - b);
  const rank = sortedValues.filter(v => v <= current).length;
  const percentile = Math.round((rank / sortedValues.length) * 100);
  
  // Determine trend from recent data
  const recentCount = Math.min(10, Math.floor(values.length / 4));
  const recentValues = values.slice(-recentCount);
  const olderValues = values.slice(-recentCount * 2, -recentCount);
  const recentAvg = recentValues.reduce((a, b) => a + b, 0) / recentValues.length;
  const olderAvg = olderValues.length > 0 
    ? olderValues.reduce((a, b) => a + b, 0) / olderValues.length 
    : recentAvg;
  
  let trend = 'stable';
  const trendThreshold = Math.abs(mean) * 0.02; // 2% of mean
  if (recentAvg > olderAvg + trendThreshold) trend = 'rising';
  if (recentAvg < olderAvg - trendThreshold) trend = 'falling';
  
  // Get date range
  const startDate = new Date(chartData[0]?.time * 1000).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  const endDate = new Date(chartData[chartData.length - 1]?.time * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  
  // Range labels
  const rangeLabels = {
    '30d': '30-day',
    '90d': '90-day',
    '1y': '1-year',
    '5y': '5-year',
    'max': 'full historical'
  };
  
  return {
    signalId,
    name: metadata.name || signalId,
    description: metadata.description || '',
    formula: metadata.formula || null,
    category: metadata.category,
    categoryName: category.name || metadata.category,
    units: metadata.units || '',
    current,
    high,
    low,
    mean,
    percentile,
    trend,
    level: currentSignal?.current?.level || 'normal',
    rangeId,
    rangeLabel: rangeLabels[rangeId] || rangeId,
    startDate,
    endDate,
    dataPoints: chartData.length,
  };
}

/**
 * Format value for display in narrative
 */
function formatValue(value, signalId) {
  if (value == null) return 'N/A';
  
  const metadata = SIGNAL_METADATA[signalId] || {};
  const units = metadata.units || '';
  
  if (signalId === 'NET_LIQUIDITY') {
    return `$${(value / 1e6).toFixed(2)}T`;
  }
  
  if (units === '%') {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  }
  
  if (units === 'bps') {
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)} bps`;
  }
  
  if (units === 'σ') {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}σ`;
  }
  
  if (Math.abs(value) >= 1000) {
    return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
  }
  
  return value.toFixed(2);
}

/**
 * Build the prompt for Claude
 */
function buildPrompt(ctx) {
  return `You are a financial markets analyst providing a brief chart narrative for a trading/investment dashboard. Generate a 2-3 sentence analysis that is:
- Insightful and actionable for traders
- Uses precise financial terminology
- References the specific data provided
- Written in present tense, authoritative tone
- NO generic statements - be specific to this signal

SIGNAL: ${ctx.name}
DESCRIPTION: ${ctx.description}
CATEGORY: ${ctx.categoryName}
${ctx.formula ? `FORMULA: ${ctx.formula}` : ''}

CURRENT DATA (${ctx.rangeLabel} range, ${ctx.startDate} to ${ctx.endDate}):
- Current Value: ${formatValue(ctx.current, ctx.signalId)}
- Range High: ${formatValue(ctx.high, ctx.signalId)}
- Range Low: ${formatValue(ctx.low, ctx.signalId)}
- Percentile: ${ctx.percentile}th (within this range)
- Trend: ${ctx.trend}
- Status Level: ${ctx.level}

Write 2-3 sentences analyzing what this signal is telling us about current market conditions. Be specific about the implications. Do not use bullet points or headers - just flowing prose.`;
}

/**
 * Call Anthropic API to generate narrative
 */
async function callClaudeAPI(prompt) {
  const apiKey = import.meta.env.VITE_ANTHROPIC_API_KEY;
  
  if (!apiKey) {
    throw new Error('ANTHROPIC_API_KEY not configured');
  }
  
  const response = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true',
    },
    body: JSON.stringify({
      model: 'claude-sonnet-4-20250514',
      max_tokens: 256,
      messages: [
        { role: 'user', content: prompt }
      ],
    }),
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error?.message || `API error: ${response.status}`);
  }
  
  const data = await response.json();
  return data.content?.[0]?.text || '';
}

/**
 * Generate narrative for a signal chart
 * 
 * @param {string} signalId - Signal identifier
 * @param {Array} chartData - Array of {time, value} points
 * @param {string} rangeId - Time range (30d, 90d, 1y, 5y, max)
 * @param {Object} currentSignal - Current signal data with level, trend, etc.
 * @returns {Promise<{text: string, cached: boolean, error: string|null}>}
 */
export async function generateNarrative(signalId, chartData, rangeId, currentSignal) {
  // Validate inputs
  if (!chartData || chartData.length < 2) {
    return { 
      text: null, 
      cached: false, 
      error: 'Insufficient data for analysis' 
    };
  }
  
  // Check cache
  const dataHash = hashChartData(chartData);
  const cacheKey = `${signalId}:${rangeId}:${dataHash}`;
  
  if (narrativeCache.has(cacheKey)) {
    return { 
      text: narrativeCache.get(cacheKey), 
      cached: true, 
      error: null 
    };
  }
  
  try {
    // Build context and prompt
    const ctx = buildContext(signalId, chartData, rangeId, currentSignal);
    const prompt = buildPrompt(ctx);
    
    // Call API
    const narrative = await callClaudeAPI(prompt);
    
    // Cache successful result
    if (narrative) {
      narrativeCache.set(cacheKey, narrative);
      
      // Limit cache size (keep last 50 narratives)
      if (narrativeCache.size > 50) {
        const firstKey = narrativeCache.keys().next().value;
        narrativeCache.delete(firstKey);
      }
    }
    
    return { 
      text: narrative, 
      cached: false, 
      error: null 
    };
    
  } catch (error) {
    console.error('[Narrative] Generation failed:', error);
    return { 
      text: null, 
      cached: false, 
      error: error.message || 'Failed to generate analysis' 
    };
  }
}

/**
 * Clear narrative cache (e.g., on page refresh)
 */
export function clearNarrativeCache() {
  narrativeCache.clear();
}

/**
 * Get cache stats for debugging
 */
export function getNarrativeCacheStats() {
  return {
    size: narrativeCache.size,
    keys: Array.from(narrativeCache.keys()),
  };
}
